import moment from 'moment';
import 'moment-timezone';
import { isEmpty } from 'lodash';
import { getPhotoURL } from "../../../utils/images";
import { formatRecurrenceLabel, formatRecurrenceLabelWithStart } from '../../../utils/Recurrence';
import { getDateFormat } from '../../../utils/time';
import { getWord } from "utils/tenant";

export default class AppointmentTimeSlot {

  constructor(slot, slotForService, showServiceProviderInfo, hasRecurringReason) {
    this.slot = slot;
    this.slotForService = slotForService || false;
    this.showServiceProviderInfo = showServiceProviderInfo || false;
    this.slotLocationName = this.locationOptions;
    this.slotDuration = this.slot.durationsMinutes[0];
    this.hasRecurringReason = hasRecurringReason;
  }

  get locationOptions() {
    if (this.slot.locations && this.slot.locations.length > 1) {
      return "Multiple appointment locations";
    }
    return isEmpty(this.slot.locations) ? "" : this.slot.locations[0].name;
  }

  get canChangeCourse() {
    // if the course context is coming from the slot, students cannot change course context selection
    return isEmpty(this.slot.courseContext);
  }

  get canChangeDuration() {
    return this.slot.durationsMinutes.length > 1;
  }

  get courseContext() {
    return this.slot.courseContext;
  }

  get courseContextId() {
    return !this.slot.courseContext ? "" : this.slot.courseContext.id;
  }

  get courseContextName() {
    return !this.slot.courseContext ? null : this.slot.courseContext.name;
  }

  get durationsMinutes() {
    return this.slot.durationsMinutes;
  }

  get firstLocationId() {
    return this.slot.locations[0].id;
  }

  get locations() {
    return this.slot.locations;
  }

  get minDurationMinutes() {
    return this.durationsMinutes[0];
  }

  get ownerId() {
    return this.slot.owner.id;
  }

  get ownerName() {
    return this.slot.owner.name;
  }

  get ownerPhotoURL() {
    return getPhotoURL(this.slot.owner.uuid, this.slot.owner.photoAvailable);
  }

  get ownerTitle() {
    return this.slot.owner.title || (this.slot.owner.roles && this.slot.owner.roles.join(", "));
  }

  get requireCourseContext() {
    return this.slot.scheduleBlock.supplementalInstruction;
  }

  get scheduleBlockId() {
    return this.slot.scheduleBlock.id;
  }

  get scheduleBlockInstructions() {
    return this.slot.scheduleBlock.instructions;
  }

  get slotTimeRangeWithRecurrence() {
    return this.hasRecurringReason ?
      formatRecurrenceLabel(this.hasRecurringReason, this.slotStartDate, this.slot.scheduleBlock.recurrenceLabel)
      : this.slotStartDate.format(getDateFormat('weekdayDateDisplayFormat'));
  }

  get slotTimeRangeWithRecurrenceStart() {
    return this.hasRecurringReason ?
      formatRecurrenceLabelWithStart(this.hasRecurringReason, this.slotStartDate, this.slot.scheduleBlock.recurrenceLabel)
      : this.slotStartDate.format(getDateFormat('weekdayDateDisplayFormat'));
  }

  get scheduleBlockType() {
    return this.slotForGroupSession ? "GROUP_SESSION" : "DEFAULT";
  }

  get groupSessionParticipants() {
    return this.slot.scheduleBlock.groupSessionParticipants;
  }

  get slotForGroupSession() {
    return this.slot.scheduleBlock.groupSession;
  }

  get slotTimeRange() {
    const startTime = this.slotStartDate.format(getDateFormat('timeDisplayFormat'));
    const endTime = this.slotStartDate.add(this.slotDuration, 'minutes').format(getDateFormat('timeDisplayFormat'));
    return startTime + " - " + endTime;
  }

  get slotStartDate() {
    return moment(this.slot.startDate).tz(this.slot.studentTimeZone);
  }

  get studentTimeZone() {
    return this.slot.studentTimeZone;
  }

  get description() {
    const location = !this.slotForService || this.showServiceProviderInfo ? this.slotLocationName : null;
    const ownerName = this.showServiceProviderInfo ? this.ownerName : null;
    const groupSession = !this.slotForGroupSession ? "" : `${getWord('groupSession')}${this.hasRecurringReason ? " " : " on "}`;
    const date = this.slotTimeRangeWithRecurrenceStart;
    const startTime = this.slotStartDate.format(getDateFormat('timeDisplayFormat'));
    const endTime = this.slotStartDate.add(this.minDurationMinutes, 'minutes').format(getDateFormat('timeDisplayFormat'));
    const name = ownerName && this.slotForService ? `with ${ownerName}` : '';
    const courseContext = this.slot.courseContext ? `for ${this.courseContextName}` : '';
    const where = location ? `at ${location}` : '';
    return `${groupSession}${date} from ${startTime} to ${endTime} ${where} ${name} ${courseContext}`.trim();
  }

  set selectedDuration(duration) {
    this.slotDuration = duration ? duration : this.slot.durationsMinutes[0];
  }

  set selectedLocationName(locationName) {
    this.slotLocationName = locationName ? locationName : this.slot.locations[0].name;
  }

  findLocationName = (locationId) => !locationId ? null
    : this.slot.locations.find(location => location.id === locationId).name;

  findLocationInstructions = (locationId) => !locationId ? null
    : this.slot.locations.find(location => location.id === locationId).instructions;

}
